import {
  DeepPartial,
  IEquatable,
  IMedia,
  IPrettify
} from '@websolutespa/bom-core';
import { ToasterProps } from 'sonner';
import { LlmBlocks } from './llm/blocks';
import { ChunkProps } from './llm/components/Chunk/chunk';
import { DEFAULT_THEME } from './llm/theme';
import { LlmSystemContext } from './useLlm/useLlm';

export type LlmAction = {
  id?: string;
  type: string;
  label: string;
  message?: string;
  icon?: string;
};

export type LlmChunkBase = {
  chunkId?: string;
  actions?: LlmAction[];
};

export type LlmChunkLog = Record<string, unknown> & {
  type: 'log';
};

export type LlmChunkError = {
  type: 'error';
  error: unknown;
};

export type LlmChunkHeader = {
  type: 'header';
  threadId: string;
  messageId: string;
};

export type LlmChunkInfo = {
  type: 'info';
  threadId: string;
};

export type LlmChunkEnd = {
  type: 'end';
};

export type LlmChunkText = {
  type: 'text';
  text: string;
};

export type LlmChunkString = {
  type: 'string';
  content: string;
}; // deprecated

export type LlmChunkTextOrString = LlmChunkText | LlmChunkString;

export function isChunkText(chunk: LlmChunkItem): chunk is LlmChunkText {
  return chunk.type === 'text';
}

/**
 * Narrows a chunk item to the known item of the given type.
 * Needed since unknown items (`Record<string, unknown>`) prevent the narrowing on `type`.
 */
export function isChunkType<T extends LlmChunkKnownItem['type']>(chunk: LlmChunkItem | undefined, type: T): chunk is Extract<LlmChunkKnownItem, { type: T }> {
  return chunk?.type === type;
}

export function isChunkString(chunk: LlmChunkItem): chunk is LlmChunkString {
  return chunk.type === 'string';
}

export function isChunkTextOrString(
  chunk: LlmChunkItem
): chunk is LlmChunkTextOrString {
  return chunk.type === 'text' || chunk.type === 'string';
}

export function isKnownChunk(
  chunk: LlmChunkItem,
  blocks: LlmBlocks
): chunk is LlmChunkKnownItem {
  return (
    typeof chunk.type === 'string' &&
    (typeof blocks[sanitizeBlockType(chunk.type)] === 'function' ||
      ['image', 'file', 'image_url'].includes(chunk.type))
  );
}

export function sanitizeBlockType(type: string): string {
  return type.replace(/Item$/, '');
}

export type LlmChunkImage = {
  type: 'image';
  url: string;
};

export type LlmChunkFile = {
  type: 'file';
  url: string;
};

export type ImageUrl = {
  url: string;
};

export type LlmChunkImageUrl = {
  type: 'image_url';
  image_url: ImageUrl;
};

/*
export type LlmChunkImage = {
  type: 'image';
  id: string;
  src: string;
};
*/

// media

export type LlmMediaImage = {
  type: 'image';
  src: string;
};

export type LlmMediaVideo = {
  type: 'video';
  src: string;
};

export type LlmMediaThron = {
  type: 'thron';
  clientId: string;
  xcontentId: string;
  sessId: string;
};

export type LlmMedia = LlmMediaImage | LlmMediaVideo | LlmMediaThron;

// actions

export type LlmChunkAction = {
  type: 'action';
  id: string;
  title: string;
};

export type LlmChunkActionItem = Omit<LlmChunkAction, 'type'> & {
  type: 'actionItem';
};

export type LlmChunkActionGroup = {
  type: 'actionGroup';
  items: (LlmChunkAction | LlmChunkActionItem)[];
};

// cards

export type LlmChunkCardBase = {
  type: 'card';
  id: string;
  date?: string;
  title: string;
  abstract: string;
  media: LlmMedia;
  cta: {
    url?: string;
    label?: string;
  };
};

export type LlmChunkCardImage = LlmChunkCardBase & {
  cardType: 'image';
  media: LlmMediaImage;
};

export type LlmChunkCardFull = LlmChunkCardBase & {
  cardType: 'full';
  media: LlmMediaImage;
};

export type LlmChunkCardVideo = LlmChunkCardBase & {
  cardType: 'video';
  media: LlmMediaVideo;
};

export type LlmChunkCardThron = LlmChunkCardBase & {
  cardType: 'thron';
  media: LlmMediaThron;
};

export type LlmChunkCard =
  | LlmChunkCardImage
  | LlmChunkCardFull
  | LlmChunkCardVideo
  | LlmChunkCardThron;

export type LlmChunkCardItem = Omit<LlmChunkCard, 'type'> & {
  type: 'cardItem';
};

export type LlmChunkCardGroup = {
  type: 'cardGroup';
  items: LlmChunkCardItem[];
};

// products

export type LlmChunkProductBase = {
  type: 'product';
  id: string;
  title: string;
  abstract?: string;
  sku?: string;
  currency?: string;
  price?: string | number;
  priceLabel?: string;
  fullPrice?: string | number;
  fullPriceLabel?: string;
  availability?: string | number;
  availabilityLabel?: string;
  label?: string;
  media: LlmMedia;
  url?: string;
  cta?: string;
};

export type LlmChunkProductSimple = LlmChunkProductBase & {
  productType: 'simple';
  media: LlmMediaImage;
};

export type LlmChunkProductConfigurable = LlmChunkProductBase & {
  productType: 'configurable';
  media: LlmMediaImage;
};

export type LlmChunkProduct =
  | LlmChunkProductSimple
  | LlmChunkProductConfigurable;

export type LlmChunkProductItem = Omit<LlmChunkProduct, 'type'> & {
  type: 'productItem';
};

export type LlmChunkProductGroup = {
  type: 'productGroup';
  items: LlmChunkProductItem[];
};

// events

export type LlmChunkEvent = {
  type: 'event';
  id: string;
  title: string;
  abstract: string;
  place: string;
  url: string;
  image: string;
  date?: string;
  time?: string;
};

export type LlmChunkEventItem = Omit<LlmChunkEvent, 'type'> & {
  type: 'eventItem';
};

export type LlmChunkEventGroup = {
  type: 'eventGroup';
  items: LlmChunkEventItem[];
};

// pois

export type LlmChunkPoi = {
  type: 'poi';
  id: string;
  title: string;
  abstract: string;
  time?: string;
  place: string;
  url: string;
  image: string;
};

export type LlmChunkPoiItem = Omit<LlmChunkPoi, 'type'> & { type: 'poiItem' };

export type LlmChunkPoiGroup = {
  type: 'poiGroup';
  items: LlmChunkPoiItem[];
};

// tripadvisor

export type LlmChunkTripadvisor = {
  type: 'tripadvisor';
  id: string;
  title: string;
  rating: number;
  reviews: number;
  priceRange: string;
  cuisine: string;
  address: string;
  url: string;
  book: string;
  image: string;
};

export type LlmChunkTripadvisorItem = Omit<LlmChunkTripadvisor, 'type'> & {
  type: 'tripadvisorItem';
};

export type LlmChunkTripadvisorGroup = {
  type: 'tripadvisorGroup';
  items: LlmChunkTripadvisorItem[];
};

// form

export type LlmChunkFormRequest = {
  type: 'formRequest';
};

export type LlmChunkFormRecap = {
  type: 'formRecap';
};

export type LlmChunkFormRecapSuccess = {
  type: 'formRecapSuccess';
};

export type LlmChunkFormRecapError = {
  type: 'formRecapError';
};

// known items

export type LlmChunkKnownItem =
  | LlmChunkEnd
  | LlmChunkAction
  | LlmChunkActionGroup
  | LlmChunkActionItem
  | LlmChunkCard
  | LlmChunkCardGroup
  | LlmChunkCardItem
  | LlmChunkProduct
  | LlmChunkProductGroup
  | LlmChunkProductItem
  | LlmChunkEvent
  | LlmChunkEventGroup
  | LlmChunkEventItem
  | LlmChunkError
  | LlmChunkFormRequest
  | LlmChunkFormRecap
  | LlmChunkFormRecapSuccess
  | LlmChunkFormRecapError
  | LlmChunkHeader
  | LlmChunkInfo
  | LlmChunkLog
  | LlmChunkPoi
  | LlmChunkPoiGroup
  | LlmChunkPoiItem
  | LlmChunkImage
  | LlmChunkFile
  | LlmChunkImageUrl
  | LlmChunkTripadvisor
  | LlmChunkTripadvisorGroup
  | LlmChunkTripadvisorItem
  | LlmChunkText
  | LlmChunkString;

export type LlmChunkUnknownItemType_ = Exclude<
  string,
  LlmChunkKnownItem['type']
>;

// !!! todo move to bom-core

type NotA<T> = T extends LlmChunkKnownItem['type'] ? never : T;
type NotB<T> = LlmChunkKnownItem['type'] extends T ? never : T;
type LlmChunkUnknownItemType = NotA<string> & NotB<string>;

/*
export type LlmChunkUnknownItem = Omit<Record<string, unknown>, 'type' | 'id'> & {
  type: LlmChunkUnknownItemType;
  id: IEquatable;
};
*/

export type LlmChunkUnknownItem = IPrettify<
  Omit<Record<string, unknown>, 'id'> & {
    id: IEquatable;
  }
>;

export type LlmChunkItem = LlmChunkKnownItem | LlmChunkUnknownItem;

export type LlmChunkType = Exclude<
  LlmChunkKnownItem['type'],
  | 'string'
  | 'text'
  | 'image'
  | 'file'
  | 'image_url'
  | 'header'
  | 'info'
  | 'end'
  | 'formRequest'
  | 'formRecap'
  | 'formRecapSuccess'
  | 'formRecapError'
>;

export type LlmItem = Extract<LlmChunkKnownItem, { type: LlmChunkType }>;

export type LlmBlock<T extends LlmChunkItem> = Omit<ChunkProps, 'item'> & {
  item: T;
  first: boolean;
  last: boolean;
};

// !!! todo
export type LlmThreadBlock<T extends LlmChunkItem | LlmChunkUnknownItem> =
  LlmBlock<T> & {
    loading: boolean;
  };

export type LlmTextBlock<T extends 'user' | 'assistant'> = Omit<
  ChunkProps,
  'item' | 'role'
> & {
  role: T;
  text: string;
  first: boolean;
  last: boolean;
};

// export type ToDiscriminatedUnion<T> = { [K in keyof T]: { type: K, value: T[K] } }[keyof T];
// type StuctureMappedType<T extends LlmChunkType> = { [K in T]: Array<LlmChunkItem<K>> };
// export type LlmChunkItems<T extends LlmChunkType = LlmChunkType> = Record<T, (item: Extract<LlmChunkItem, { type: T }>) => string>;

export type LlmChunk = string | LlmChunkItem;

export type LlmCredentials = {
  apiKey: string;
  appKey: string;
};

export type LlmMessage = {
  role: string; // assistant, user
  content: string | LlmChunkItem[];
  messageId?: string;
  threadId?: string;
  createdAt?: string;
};

export type LlmHistory = LlmDecodedThread & {
  messageId: string;
  createdAt?: string;
  user: LlmDecodedMessage & {
    text: string;
  };
  assistant: LlmDecodedMessage & {
    text: string;
  };
};

export type LlmStoredHistory = {
  threadId: string;
  messageId: string;
};

export type LlmSerializedMessage = {
  role: string; // assistant, user
  content: string;
  messageId?: string;
};

export type LlmDecodedMessage = LlmMessage & {
  chunks: LlmChunkItem[];
};

export type StreamRequest = LlmCredentials & {
  messages: LlmMessage[];
  threadId?: string;
  systemContext?: LlmSystemContext;
};

export type StreamResponse = {
  chunks: LlmChunkItem[];
  threadId: string;
  messageId: string;
};

export type LlmCard = {
  id?: string;
  blockType?: string;
  blockName?: string;
  icon?: IMedia;
  body: string;
  title: string;
  message?: string;
};

export type LlmLogo = {
  logo: IMedia;
};

export type LlmSampleInputText = {
  id?: string;
  sampleInputText: string;
};

export type LlmHeaderCta = {
  id?: string;
  title: string;
  href: string;
  target: string;
  icon?: IMedia;
};

/*
export type LlmTheme = Omit<{
  [key: string]: LlmThemeItem | CSSProperties;
}, keyof LlmThemeProps> & LlmThemeProps;
*/

/*
export type LlmThemeBase = {
  [key: string]: CSSProperties | string | number | boolean | LlmThemeBase;
};

export type LlmTheme = LlmThemeBase & LlmThemeProps;
*/

export type LlmTheme = DeepPartial<typeof DEFAULT_THEME> & {
  toaster?: ToasterProps['theme'];
};

export type LlmRecognitionMode = ('none' | 'default') & string;
export type LlmSynthesisMode = ('none' | 'default' | 'elevenlabs') & string;
export type LlmStorageMode = ('local' | 'session') & string;

export type LlmContents = {
  additionalLogos?: LlmLogo[];
  collapsedWelcomeText?: string;
  collapsedWelcomeTextCta?: string;
  collapsedWelcomeTextHover?: string;
  customIntro?: string;
  customTheme?: DeepPartial<LlmTheme>;
  disablePoweredBy?: boolean;
  disableSpeechRecognition?: boolean;
  disableSpeechSynthesis?: boolean;
  disclaimer?: string;
  disclaimerFooter?: boolean;
  disclaimerShort?: string;
  enableFeedback?: boolean;
  enableHistory?: boolean;
  enableMinimize?: boolean;
  enableUpload?: boolean;
  extendedWelcomeText?: string;
  headerCtas?: LlmHeaderCta[];
  intro?: string;
  layoutBuilder?: LlmCard[];
  logo?: IMedia | null;
  logoMini?: IMedia | null;
  maxFileSize?: number;
  mimeTypes?: string;
  openingSound?: IMedia | null;
  promptPlaceholder?: string;
  promptTokenLimit?: number;
  recognitionMode?: LlmRecognitionMode;
  sampleInputTexts?: LlmSampleInputText[];
  shortWelcomeText?: string;
  storageMode?: LlmStorageMode;
  synthesisMode?: LlmSynthesisMode;
  textToSpeechApiKey?: string;
  textToSpeechVoiceId?: string;
};

export type LlmThread = {
  messages: LlmMessage[];
  threadId: string;
};

export type LlmDecodedThread = {
  messages: LlmDecodedMessage[];
  threadId: string;
};

export type LlmWebhook = {
  webhook: string;
};

export type LlmWebhooks = LlmWebhook[];

export type LlmMode = ('site' | 'chat' | 'url2text' | 'translation') & string;

export type LlmApp = {
  mode: LlmMode;
  contents: LlmContents;
  thread?: LlmThread;
  webhooks?: LlmWebhooks;
};

export type LlmMockApp =
  | {
      mode: LlmMode;
      contents: LlmContents;
    }
  | Record<
      string,
      {
        mode: LlmMode;
        contents: LlmContents;
      }
    >;

export type LlmMockThreadMessage = {
  chunks: LlmChunk[];
  role: string;
  createdAt?: string;
};

export type LlmMockThread =
  | LlmMockThreadMessage[]
  | Record<string, LlmMockThreadMessage[]>;

export type LlmMock = {
  app: LlmMockApp;
  thread: LlmMockThread;
};

export type LlmTest = undefined | boolean | DeepPartial<LlmMock>;

export type LlmVars = { [key: string]: string };

export type LlmInstance = {
  open: () => void;
  setOpened: (opened: boolean) => void;
  isOpened: () => boolean;
  send: (message: string) => void;
  clear: () => void;
  // dispose: () => void;
};

export type LlmUiStatus = {
  open: boolean;
};

export type FormRecapData = {
  fullName: string;
  email: string;
  privacy: string;
  threadId: string;
};

export type PostSummaryOptions = FormRecapData & {
  html: string;
};

export type PostFeedback = {
  threadId: string;
  messageId: string;
  feedbackRating: number;
  feedbackMessage?: string;
};

export const LLM_DEFAULT_MIME_TYPES =
  '.jpg, .jpeg, .png, .svg, .webp, .txt, .md, .pdf, .csv, .doc, .xls, .ppt';
export const LLM_DEFAULT_MAX_FILE_SIZE = 1048576; // 1Mb
// export const LLM_DEFAULT_MAX_FILE_SIZE = 2097152; // 2Mb
